import {useState, useMemo, useEffect, useCallback, startTransition, type CSSProperties} from "react";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useLocation, useSearch} from "wouter";
import {useDroppable, useDraggable, type DragStartEvent, type DragOverEvent} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {libraryFolderTreeDragStateRef, librarySidebarDndUiRef} from "@/library/libraryRouteDragEnd";
import {
    ArrowLeft,
    ChevronRight,
    ChevronDown,
    Folder,
    FolderPlus,
    FolderOpen,
    MoreHorizontal,
    Pencil,
    Trash2,
    PanelLeftClose,
    FileText,
    BookOpen,
    Search,
    GripVertical,
    Brain,
    Sparkles,
    Wand2,
    Bot,
    GitBranch,
    Loader2,
    ArrowDownAZ,
    ArrowUpZA,
    Clock,
    Bookmark,
    Tag,
} from "lucide-react";
import {
    librarySectionHeadingClassName,
    spaceNavRowClassName,
    spaceNavRowSelectedClassName,
    spaceNavLabelClassName,
    topicNavRowClassName,
    topicNavRowSelectedClassName,
} from "@/components/library/libraryNavStyles";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {ScrollArea} from "@/components/ui/scroll-area";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {Label} from "@/components/ui/label";
import {Progress} from "@/components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {SiNotion, SiGithub, SiFigma} from "react-icons/si";
import {cn} from "@/lib/utils";
import {useLibrary} from "@/contexts/LibraryContext";
import {foldersService, type LibraryFolder} from "@/services/foldersService";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {useToast} from "@/hooks/use-toast";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {useMastermind} from "@/contexts/MastermindContext";
import {githubIntegrationService} from "@/services/githubIntegrationService";
import { startGitHubOAuth, disconnectGitHub } from "@/services/githubOAuthService";
import { supabase } from "@/lib/supabase";
import type {MastermindSpace, Topic} from "@shared/schema";
import {topicsService} from "@/services/topicsService";
import {GitHubFilePicker} from "@/components/GitHubFilePicker";
import {knowledgeBaseService, type KnowledgeBaseItem} from "@/services/knowledgeBaseService";
import {useMergedKnowledgeDocuments} from "@/hooks/useMergedKnowledgeDocuments";
import { collectSpaceIdsForCounts } from "@/lib/kbDocumentLinks";
import {schedulePrefetchKnowledgeBaseItem} from "@/lib/prefetchKnowledgeBaseItem";
import {sourcesService} from "@/services/sourcesService";
import type {Source} from "@shared/schema";
import {Badge} from "@/components/ui/badge";
import {LibrarySidebarQuickAccess} from "@/components/library/LibrarySidebarQuickAccess";
import {
    useLibraryFavorites,
    useLibraryRecent,
    resolveQuickAccessDocs,
} from "@/hooks/useLibraryQuickAccess";
import type {LibraryDocRef} from "@/lib/libraryQuickAccess";

function collectTopicIdsForCounts(item: KnowledgeBaseItem): string[] {
    const ids = new Set<string>();
    if (item.topicId) ids.add(String(item.topicId));
    (item.topicIds ?? []).forEach((t) => ids.add(String(t)));
    let meta: Record<string, unknown> = {};
    if (item.metadata) {
        if (typeof item.metadata === "object") meta = item.metadata as Record<string, unknown>;
        else if (typeof item.metadata === "string") {
            try {
                meta = JSON.parse(item.metadata) as Record<string, unknown>;
            } catch {
                meta = {};
            }
        }
    }
    if (meta.topicId) ids.add(String(meta.topicId));
    if (Array.isArray(meta.topicIds)) (meta.topicIds as unknown[]).forEach((t) => ids.add(String(t)));
    return [...ids];
}

interface FolderTreeItemProps {
    folder: LibraryFolder;
    allFolders: LibraryFolder[];
    level: number;
    onSelect: (folderId: string) => void;
    onEdit: (folder: LibraryFolder) => void;
    onDelete: (folder: LibraryFolder) => void;
    onNewSubfolder: (folder: LibraryFolder) => void;
    onDocumentClick: (documentId: string, itemType?: "note" | "reference") => void;
    activeId: string | null;
    overId: string | null;
    getItemCount: (folderId: string) => number;
    getItems: (folderId: string) => Array<{id: string; title: string; type: "note" | "reference"}>;
}

interface DraggableDocumentItemProps {
    item: {id: string; title: string; type: "note" | "reference"};
    paddingLeft: number;
    onClick: () => void;
}

function DraggableDocumentItem({item, paddingLeft, onClick}: DraggableDocumentItemProps) {
    const queryClient = useQueryClient();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: `doc-${item.id}`,
        data: {type: "document", documentId: item.id, documentType: item.type, itemType: item.type},
    });

    const style: React.CSSProperties = {
        paddingLeft: `${paddingLeft}px`,
        ...(transform ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            opacity: isDragging ? 0.5 : 1,
            zIndex: isDragging ? 1000 : undefined,
        } : {})
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                "flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer group",
                isDragging && "bg-accent/50 shadow-md"
            )}
            onPointerEnter={() => {
                if (item.type === "note") schedulePrefetchKnowledgeBaseItem(queryClient, item.id);
            }}
            onClick={onClick}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
            >
                <GripVertical className="h-3 w-3 text-muted-foreground"/>
            </div>
            <FileText className="h-3 w-3 text-muted-foreground shrink-0"/>
            <span className="text-xs truncate flex-1">{item.title}</span>
            <Badge variant="outline" className="text-[10px] h-4 px-1">
                {item.type === "note" ? "Note" : "Ref"}
            </Badge>
        </div>
    );
}

function SidebarSpaceDropShell({
    spaceId,
    className,
    style,
    "data-testid": dataTestId,
    children,
}: {
    spaceId: string;
    className?: string;
    style?: CSSProperties;
    "data-testid"?: string;
    children: React.ReactNode;
}) {
    const {setNodeRef, isOver} = useDroppable({
        id: `droppable-space-${spaceId}`,
        data: {spaceId},
    });
    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={dataTestId}
            className={cn(className, isOver && "ring-2 ring-primary ring-offset-1 bg-purple-100/80 dark:bg-purple-900/40")}
        >
            {children}
        </div>
    );
}

function SidebarTopicDropShell({
    topicId,
    spaceId,
    className,
    style,
    children,
}: {
    topicId: string;
    spaceId: string | null | undefined;
    className?: string;
    style?: CSSProperties;
    children: React.ReactNode;
}) {
    const {setNodeRef, isOver} = useDroppable({
        id: `droppable-topic-${topicId}`,
        data: {topicId, spaceId},
    });
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(className, isOver && "ring-2 ring-primary ring-offset-1 bg-primary/10 dark:bg-primary/20")}
        >
            {children}
        </div>
    );
}

function SortableFolderTreeItem({
                                    folder,
                                    allFolders,
                                    level,
                                    onSelect,
                                    onEdit,
                                    onDelete,
                                    onNewSubfolder,
                                    onDocumentClick,
                                    activeId,
                                    overId,
                                    getItemCount,
                                    getItems
                                }: FolderTreeItemProps) {
    const {selectedFolderId, expandedFolderIds, toggleFolderExpanded} = useLibrary();
    const childFolders = foldersService.getChildFolders(allFolders, folder.id);
    const hasChildren = childFolders.length > 0;
    const itemCount = getItemCount(folder.id);
    const folderItems = getItems(folder.id);
    const hasItems = itemCount > 0;
    const isExpanded = expandedFolderIds.has(folder.id);
    const isSelected = selectedFolderId === folder.id;
    const isDropTarget = overId === folder.id && activeId !== folder.id;

    const {
        attributes,
        listeners,
        setNodeRef: setSortableRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: folder.id});

    const {setNodeRef: setDroppableRef, isOver} = useDroppable({
        id: `droppable-${folder.id}`,
        data: {folderId: folder.id},
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const setNodeRef = (node: HTMLDivElement | null) => {
        setSortableRef(node);
        setDroppableRef(node);
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div
                className={cn(
                    "group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors",
                    isSelected && "bg-primary/10 border border-primary/30 shadow-sm",
                    isDragging && "bg-accent/50 shadow-md",
                    (isDropTarget || isOver) && "ring-2 ring-primary ring-offset-1 bg-primary/10"
                )}
                style={{paddingLeft: level > 0 ? `${level * 16}px` : undefined}}
                onClick={() => onSelect(folder.id)}
            >
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 mr-1"
                    onClick={(e) => e.stopPropagation()}
                >
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground"/>
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 p-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleFolderExpanded(folder.id);
                    }}
                >
                    {(hasChildren || hasItems) ? (
                        isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5"/>
                        ) : (
                            <ChevronRight className="h-3.5 w-3.5"/>
                        )
                    ) : (
                        <div className="w-3.5"/>
                    )}
                </Button>

                {isExpanded ? (
                    <FolderOpen className="h-4 w-4 text-amber-500 shrink-0"/>
                ) : (
                    <Folder className="h-4 w-4 text-amber-500 shrink-0"/>
                )}

                <span className="text-sm truncate flex-1">{folder.name}</span>
                
                {itemCount > 0 && (
                    <Badge variant="secondary" className="text-xs h-5 px-1.5">
                        {itemCount}
                    </Badge>
                )}

                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        >
                            <MoreHorizontal className="h-3.5 w-3.5"/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onNewSubfolder(folder)}>
                            <FolderPlus className="h-4 w-4 mr-2"/> New subfolder
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(folder)}>
                            <Pencil className="h-4 w-4 mr-2"/> Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => onDelete(folder)}
                        >
                            <Trash2 className="h-4 w-4 mr-2"/> Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {(hasChildren || hasItems) && isExpanded && (
                <div>
                    {hasItems && (
                        <div className="mt-1 space-y-0.5">
                            {folderItems.slice(0, 10).map((item) => (
                                <DraggableDocumentItem
                                    key={item.id}
                                    item={item}
                                    paddingLeft={(level + 2) * 16 + 18}
                                    onClick={() =>
                                        onDocumentClick(
                                            item.id,
                                            item.type === "reference" ? "reference" : "note"
                                        )
                                    }
                                />
                            ))}
                            {folderItems.length > 10 && (
                                <div className="px-2 py-1 text-xs text-muted-foreground" style={{paddingLeft: `${(level + 2) * 16 + 18}px`}}>
                                    +{folderItems.length - 10} more
                                </div>
                            )}
                        </div>
                    )}
                    {hasChildren && (
                        <div>
                            {childFolders.map((child) => (
                                <SortableFolderTreeItem
                                    key={child.id}
                                    folder={child}
                                    allFolders={allFolders}
                                    level={level + 1}
                                    onSelect={onSelect}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    onNewSubfolder={onNewSubfolder}
                                    onDocumentClick={onDocumentClick}
                                    activeId={activeId}
                                    overId={overId}
                                    getItemCount={getItemCount}
                                    getItems={getItems}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function LibrarySidebar() {
    const [, navigate] = useLocation();
    const searchString = useSearch();
    const activeTopicFilterId = useMemo(() => {
        const params = new URLSearchParams(searchString);
        return params.get("topic");
    }, [searchString]);
    const activeLibraryView = useMemo((): "favorites" | "recent" | null => {
        const params = new URLSearchParams(searchString);
        const view = params.get("view");
        return view === "favorites" || view === "recent" ? view : null;
    }, [searchString]);
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {toast} = useToast();
    const queryClient = useQueryClient();
    const {
        isLibrarySidebarOpen,
        toggleLibrarySidebar,
        selectedFolderId,
        setSelectedFolderId,
        expandedFolderIds,
        toggleFolderExpanded,
        setSelectedDocumentId,
        setSelectedReferenceId,
    } = useLibrary();
    const {setIsCollapsed} = useMastermind();

    const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
    const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
    const [editingFolder, setEditingFolder] = useState<LibraryFolder | null>(null);
    const [folderName, setFolderName] = useState("");
    const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    // Fetch folders — same as Library page & RightSidebar: Personal = all folders (no arg), Workspace = that workspace's folders
    const folderWorkspaceParam = currentWorkspaceId ?? undefined;
    const {data: folders = [], isLoading} = useQuery({
        queryKey: ["/api/library-folders", user?.id, folderWorkspaceParam === undefined ? "all" : folderWorkspaceParam ?? "personal"],
        queryFn: async () => {
            if (!user) return [];
            return await foldersService.getFolders(folderWorkspaceParam);
        },
        enabled: !!user,
        staleTime: 60000, // Cache for 1 minute to reduce database load
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    });

    const spacesQuery = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", user?.id, currentWorkspaceId ?? "personal"],
        queryFn: async () => mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null),
        enabled: !!user,
        refetchOnMount: true,
        staleTime: 0,
    });
    const spaces = spacesQuery.data ?? [];
    // Fetch topics — Personal: same query key and no-arg call as Library page (share cache, show all topics). Workspace: scope by workspace.
    const isPersonal = currentWorkspaceId == null;
    const {data: topics = []} = useQuery<Topic[]>({
        queryKey: isPersonal ? ["/api/topics/all", user?.id] : ["/api/topics/all", user?.id, currentWorkspaceId],
        queryFn: async () => {
            if (!user) return [];
            return await topicsService.getAllTopics(isPersonal ? undefined : currentWorkspaceId ?? undefined);
        },
        enabled: !!user,
        staleTime: 60000,
    });

    const { allDocuments: kbItems = [] } = useMergedKnowledgeDocuments({ enabled: !!user });
    const {favorites, toggleFavorite} = useLibraryFavorites();
    const {recent, touchRecent} = useLibraryRecent();

    const {data: sources = []} = useQuery<Source[]>({
        queryKey: isPersonal ? ["/api/sources", user?.id] : ["/api/sources", user?.id, currentWorkspaceId],
        queryFn: async () => {
            if (!user) return [];
            return await sourcesService.getSources(undefined, isPersonal ? null : currentWorkspaceId);
        },
        enabled: !!user,
        staleTime: 30000,
    });

    const [aiToolkitOpen, setAiToolkitOpen] = useState(false);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [integrationsOpen, setIntegrationsOpen] = useState(true);
    const [sortOrder, setSortOrder] = useState<"recent" | "a-z" | "z-a">("recent");
    const [showGitHubPicker, setShowGitHubPicker] = useState(false);
    const [isImportingGitHubFile, setIsImportingGitHubFile] = useState(false);

    // Fetch GitHub connection status
    const { data: githubStatus } = useQuery({
        queryKey: ["github-integration-status", user?.id],
        queryFn: async () => {
            if (!user) return { connected: false };
            return await githubIntegrationService.getConnectionStatus();
        },
        enabled: !!user,
        staleTime: 60000,
    });

    const handleConnectGitHub = async () => {
        try {
            await startGitHubOAuth();
        } catch (error: unknown) {
            toast({
                title: "Connection Failed",
                description: error instanceof Error ? error.message : "Failed to connect GitHub",
                variant: "destructive",
            });
        }
    };

    const handleDisconnectGitHub = async () => {
        try {
            await disconnectGitHub();
            queryClient.invalidateQueries({ queryKey: ["github-integration-status"] });
            queryClient.invalidateQueries({ queryKey: ["github-user-repos"] });
            toast({
                title: "GitHub Disconnected",
                description: "Your GitHub account has been disconnected",
            });
        } catch (error: unknown) {
            toast({
                title: "Disconnect Failed",
                description: error instanceof Error ? error.message : "Failed to disconnect GitHub",
                variant: "destructive",
            });
        }
    };

    const createFolderMutation = useMutation({
        mutationFn: async ({ name, parentId }: { name: string; parentId: string | null }) => {
            return await foldersService.createFolder({
                name,
                parentId: parentId
            }, currentWorkspaceId ?? null);
        },
        onSuccess: () => {
            toast({title: "Folder created"});
            queryClient.invalidateQueries({queryKey: ["/api/library-folders"]});
            setNewFolderDialogOpen(false);
            setFolderName("");
            setNewFolderParentId(null);
        },
        onError: (error) => {
            toast({
                title: "Error creating folder",
                description: error instanceof Error ? error.message : "Failed to create folder",
                variant: "destructive"
            });
        },
    });

    const updateFolderMutation = useMutation({
        mutationFn: async ({id, name}: { id: string; name: string }) => {
            return await foldersService.updateFolder(id, {name});
        },
        onSuccess: () => {
            toast({title: "Folder renamed"});
            queryClient.invalidateQueries({queryKey: ["/api/library-folders"]});
            setEditFolderDialogOpen(false);
            setEditingFolder(null);
            setFolderName("");
        },
        onError: (error) => {
            toast({
                title: "Error renaming folder",
                description: error instanceof Error ? error.message : "Failed to rename folder",
                variant: "destructive"
            });
        },
    });

    const deleteFolderMutation = useMutation({
        mutationFn: async (id: string) => {
            return await foldersService.deleteFolder(id);
        },
        onSuccess: () => {
            toast({title: "Folder deleted"});
            queryClient.invalidateQueries({queryKey: ["/api/library-folders"]});
            if (selectedFolderId === editingFolder?.id) {
                setSelectedFolderId(null);
            }
        },
        onError: (error) => {
            toast({
                title: "Error deleting folder",
                description: error instanceof Error ? error.message : "Failed to delete folder",
                variant: "destructive"
            });
        },
    });

    const handleBackToMenu = () => {
        navigate("/");
    };

    const handleSelectFolder = (folderId: string) => {
        setSelectedFolderId(selectedFolderId === folderId ? null : folderId);
    };

    const handleEditFolder = (folder: LibraryFolder) => {
        setEditingFolder(folder);
        setFolderName(folder.name);
        setEditFolderDialogOpen(true);
    };

    const handleDeleteFolder = (folder: LibraryFolder) => {
        if (confirm(`Are you sure you want to delete "${folder.name}"?`)) {
            deleteFolderMutation.mutate(folder.id);
        }
    };

    const handleNewSubfolder = (folder: LibraryFolder) => {
        setNewFolderParentId(folder.id);
        setFolderName("");
        setNewFolderDialogOpen(true);
    };

    const handleCreateFolder = () => {
        if (folderName.trim()) {
            createFolderMutation.mutate({ name: folderName.trim(), parentId: newFolderParentId });
        }
    };

    const favoriteDocs = useMemo(
        () => resolveQuickAccessDocs(favorites, kbItems, sources),
        [favorites, kbItems, sources]
    );
    const recentDocs = useMemo(
        () => resolveQuickAccessDocs(recent, kbItems, sources),
        [recent, kbItems, sources]
    );

    const handleDocumentClick = (
        documentId: string,
        itemType: "note" | "reference" = "note"
    ) => {
        touchRecent({id: documentId, itemType});
        setIsCollapsed(false);
        if (itemType === "reference") {
            setSelectedDocumentId(null);
            setSelectedReferenceId(documentId);
        } else {
            setSelectedReferenceId(null);
            setSelectedDocumentId(documentId);
        }
        const viewSuffix = activeLibraryView ? `?view=${activeLibraryView}` : "";
        navigate(`/library${viewSuffix}`);
    };

    const handleQuickAccessDocumentClick = (
        ref: LibraryDocRef,
        view: "favorites" | "recent"
    ) => {
        setSelectedFolderId(null);
        touchRecent(ref);
        setIsCollapsed(false);
        if (ref.itemType === "reference") {
            setSelectedDocumentId(null);
            setSelectedReferenceId(ref.id);
        } else {
            setSelectedReferenceId(null);
            setSelectedDocumentId(ref.id);
        }
        navigate(`/library?view=${view}`);
    };

    const handleUnfavoriteFromSidebar = async (ref: LibraryDocRef) => {
        try {
            await toggleFavorite(ref);
        } catch {
            toast({
                title: "Could not update favorites",
                variant: "destructive",
            });
        }
    };

    const rootFolders = foldersService.buildFolderTree(folders);
    
    // Sort folders based on sortOrder
    const sortedRootFolders = useMemo(() => {
        const sorted = [...rootFolders];
        switch (sortOrder) {
            case "a-z":
                sorted.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case "z-a":
                sorted.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case "recent":
            default:
                sorted.sort((a, b) => {
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return dateB - dateA;
                });
                break;
        }
        return sorted;
    }, [rootFolders, sortOrder]);

    // Combined folders + spaces, sorted together by sortOrder (so both follow the same sort)
    type CombinedItem = { type: "folder"; data: LibraryFolder } | { type: "space"; data: MastermindSpace };
    const combinedSortedList = useMemo((): CombinedItem[] => {
        const dedupedSpaces = Array.from(new Map(spaces.map((s) => [s.id, s])).values());
        const items: CombinedItem[] = [
            ...sortedRootFolders.map((f) => ({ type: "folder" as const, data: f })),
            ...dedupedSpaces.map((s) => ({ type: "space" as const, data: s })),
        ];
        const sortFn = (a: CombinedItem, b: CombinedItem) => {
            const nameA = a.data.name || "";
            const nameB = b.data.name || "";
            const dateA = a.data.updatedAt ? new Date(a.data.updatedAt).getTime() : (a.data.createdAt ? new Date(a.data.createdAt).getTime() : 0);
            const dateB = b.data.updatedAt ? new Date(b.data.updatedAt).getTime() : (b.data.createdAt ? new Date(b.data.createdAt).getTime() : 0);
            if (sortOrder === "a-z") return nameA.localeCompare(nameB);
            if (sortOrder === "z-a") return nameB.localeCompare(nameA);
            return dateB - dateA; // recent first
        };
        items.sort(sortFn);
        return items;
    }, [sortedRootFolders, spaces, sortOrder]);

    const sortedSpacesList = useMemo(() => {
        const deduped = Array.from(new Map(spaces.map((s) => [s.id, s])).values());
        const sorted = [...deduped];
        switch (sortOrder) {
            case "a-z":
                sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                break;
            case "z-a":
                sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
                break;
            case "recent":
            default:
                sorted.sort((a, b) => {
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return dateB - dateA;
                });
                break;
        }
        return sorted;
    }, [spaces, sortOrder]);

    const filteredSpacesList = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sortedSpacesList;
        return sortedSpacesList.filter((s) => (s.name || "").toLowerCase().includes(q));
    }, [sortedSpacesList, searchQuery]);
    
    const getAllFoldersFlat = (folders: LibraryFolder[]): LibraryFolder[] => {
        const result: LibraryFolder[] = [];
        const traverse = (folderList: LibraryFolder[]) => {
            folderList.forEach(folder => {
                result.push(folder);
                const children = foldersService.getChildFolders(folders, folder.id);
                if (children.length > 0) {
                    traverse(children);
                }
            });
        };
        traverse(rootFolders);
        return result;
    };
    const allFoldersFlat = getAllFoldersFlat(folders);

    // Calculate item counts and items per folder
    const folderItemCounts = useMemo(() => {
        const counts = new Map<string, number>();
        const items = new Map<string, Array<{id: string; title: string; type: "note" | "reference"}>>();
        
        // Count notes
        kbItems.forEach((item) => {
            if (item.folderId) {
                const count = counts.get(item.folderId) || 0;
                counts.set(item.folderId, count + 1);
                
                const folderItems = items.get(item.folderId) || [];
                folderItems.push({
                    id: item.id,
                    title: item.title || "Untitled Note",
                    type: "note"
                });
                items.set(item.folderId, folderItems);
            }
        });
        
        // Count sources/references
        sources.forEach((source: any) => {
            if (source.folderId) {
                const count = counts.get(source.folderId) || 0;
                counts.set(source.folderId, count + 1);
                
                const folderItems = items.get(source.folderId) || [];
                folderItems.push({
                    id: source.id,
                    title: source.title || "Untitled Reference",
                    type: "reference"
                });
                items.set(source.folderId, folderItems);
            }
        });
        
        return { counts, items };
    }, [kbItems, sources]);

    const getFolderItemCount = (folderId: string): number => {
        return folderItemCounts.counts.get(folderId) || 0;
    };

    const getFolderItems = (folderId: string): Array<{id: string; title: string; type: "note" | "reference"}> => {
        return folderItemCounts.items.get(folderId) || [];
    };

    // Calculate item counts and items per space
    const spaceItemCounts = useMemo(() => {
        const counts = new Map<string, number>();
        const items = new Map<string, Array<{id: string; title: string; type: "note" | "reference"}>>();
        
        // Count notes by space (primary + metadata spaceIds)
        kbItems.forEach((item) => {
            for (const spaceId of collectSpaceIdsForCounts(item)) {
                const count = counts.get(spaceId) || 0;
                counts.set(spaceId, count + 1);
                const spaceItems = items.get(spaceId) || [];
                spaceItems.push({
                    id: item.id,
                    title: item.title || "Untitled Note",
                    type: "note"
                });
                items.set(spaceId, spaceItems);
            }
        });
        
        return { counts, items };
    }, [kbItems]);

    const getSpaceItemCount = (spaceId: string): number => {
        return spaceItemCounts.counts.get(spaceId) || 0;
    };

    const getSpaceItems = (spaceId: string): Array<{id: string; title: string; type: "note" | "reference"}> => {
        return spaceItemCounts.items.get(spaceId) || [];
    };

    const topicItemCounts = useMemo(() => {
        const counts = new Map<string, number>();
        const items = new Map<string, Array<{id: string; title: string; type: "note" | "reference"}>>();
        kbItems.forEach((item) => {
            for (const topicId of collectTopicIdsForCounts(item)) {
                counts.set(topicId, (counts.get(topicId) || 0) + 1);
                const list = items.get(topicId) || [];
                list.push({ id: item.id, title: item.title || "Untitled Note", type: "note" });
                items.set(topicId, list);
            }
        });
        sources.forEach((source) => {
            const ids: string[] = Array.isArray((source as { linkedTopicIds?: string[] }).linkedTopicIds)
                ? (source as { linkedTopicIds: string[] }).linkedTopicIds
                : Array.isArray((source as { topics?: string[] }).topics)
                    ? (source as { topics: string[] }).topics
                    : [];
            ids.forEach((topicId: string) => {
                if (!topicId) return;
                counts.set(topicId, (counts.get(topicId) || 0) + 1);
                const list = items.get(topicId) || [];
                list.push({ id: source.id, title: source.title || "Untitled Reference", type: "reference" });
                items.set(topicId, list);
            });
        });
        return { counts, items };
    }, [kbItems, sources]);

    const getTopicItemCount = (topicId: string) => topicItemCounts.counts.get(topicId) || 0;
    const getTopicItems = (topicId: string) => topicItemCounts.items.get(topicId) || [];

    const handleUpdateFolder = () => {
        if (editingFolder && folderName.trim()) {
            updateFolderMutation.mutate({id: editingFolder.id, name: folderName.trim()});
        }
    };

    const filteredFolders = searchQuery
        ? folders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : rootFolders;

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    }, []);

    const handleDragOver = useCallback((event: DragOverEvent) => {
        const overIdRaw = event.over?.id;
        if (typeof overIdRaw === "string") {
            if (overIdRaw.startsWith("droppable-space-") || overIdRaw.startsWith("droppable-topic-")) {
                setOverId(null);
            } else if (overIdRaw.startsWith("droppable-")) {
                setOverId(overIdRaw.replace("droppable-", ""));
            } else {
                setOverId(overIdRaw);
            }
        } else {
            setOverId(null);
        }
    }, []);

    useEffect(() => {
        librarySidebarDndUiRef.current = {
            onDragStart: handleDragStart,
            onDragOver: handleDragOver,
            resetUi: () => {
                setActiveId(null);
                setOverId(null);
            },
        };
        return () => {
            librarySidebarDndUiRef.current = null;
        };
    }, [handleDragStart, handleDragOver]);

    useEffect(() => {
        libraryFolderTreeDragStateRef.current = {
            folders,
            searchQuery,
            filteredFolders,
            combinedSortedList,
        };
        return () => {
            libraryFolderTreeDragStateRef.current = null;
        };
    }, [folders, searchQuery, filteredFolders, combinedSortedList]);

    if (!isLibrarySidebarOpen) {
        return (
            <div className="w-12 border-r bg-background flex flex-col items-center py-2 gap-2 shrink-0">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleLibrarySidebar}
                    title="Expand sidebar"
                >
                    <BookOpen className="h-5 w-5"/>
                </Button>
            </div>
        );
    }

    return (
        <div className="min-w-[200px] w-full border-r bg-background flex flex-col h-full">
            <div className="p-3 border-b flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    onClick={handleBackToMenu}
                >
                    <ArrowLeft className="h-4 w-4"/>
                    Back to Menu
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={toggleLibrarySidebar}
                >
                    <PanelLeftClose className="h-4 w-4"/>
                </Button>
            </div>

            <div className="p-3 border-b">
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                    <Input
                        placeholder="Search folders..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 h-8 text-sm"
                    />
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-2">
                    <LibrarySidebarQuickAccess
                        activeView={activeLibraryView}
                        favoriteDocs={favoriteDocs}
                        recentDocs={recentDocs}
                        onNavigateFavorites={() => {
                            setSelectedFolderId(null);
                            navigate("/library?view=favorites");
                        }}
                        onNavigateRecent={() => {
                            setSelectedFolderId(null);
                            navigate("/library?view=recent");
                        }}
                        onDocumentClick={handleQuickAccessDocumentClick}
                        onUnfavorite={handleUnfavoriteFromSidebar}
                    />
                    {/* My Library header with toggle and + button */}
                    <div
                        className={cn(
                            "flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors",
                            selectedFolderId === null && "bg-accent"
                        )}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0 p-0.5"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleFolderExpanded("root");
                            }}
                            data-testid="toggle-my-library"
                        >
                            {expandedFolderIds.has("root") ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground"/>
                            ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground"/>
                            )}
                        </Button>
                        <div 
                            className="flex items-center gap-2 flex-1"
                            onClick={() => {
                                setSelectedFolderId(null);
                                navigate("/library");
                            }}
                        >
                            <BookOpen className="h-4 w-4 text-blue-500"/>
                            <span className="text-sm font-medium">My Library</span>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid="button-sort-library"
                                >
                                    {sortOrder === "a-z" ? (
                                        <ArrowDownAZ className="h-4 w-4 text-muted-foreground"/>
                                    ) : sortOrder === "z-a" ? (
                                        <ArrowUpZA className="h-4 w-4 text-muted-foreground"/>
                                    ) : (
                                        <Clock className="h-4 w-4 text-muted-foreground"/>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                    onClick={() => setSortOrder("recent")}
                                    className={sortOrder === "recent" ? "bg-accent" : ""}
                                >
                                    <Clock className="h-4 w-4 mr-2"/> Most Recent
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => setSortOrder("a-z")}
                                    className={sortOrder === "a-z" ? "bg-accent" : ""}
                                >
                                    <ArrowDownAZ className="h-4 w-4 mr-2"/> A to Z
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => setSortOrder("z-a")}
                                    className={sortOrder === "z-a" ? "bg-accent" : ""}
                                >
                                    <ArrowUpZA className="h-4 w-4 mr-2"/> Z to A
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                                e.stopPropagation();
                                setFolderName("");
                                setNewFolderParentId(selectedFolderId);
                                setNewFolderDialogOpen(true);
                            }}
                            data-testid="button-new-folder"
                        >
                            <FolderPlus className="h-4 w-4 text-muted-foreground"/>
                        </Button>
                    </div>

                    {/* Collapsible content under My Library */}
                    {expandedFolderIds.has("root") && (
                        <>
                            <div className="mt-1 ml-4 space-y-3">
                                {isLoading ? (
                                        <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                                            Loading folders...
                                        </div>
                                    ) : (
                                    <>
                                        {(searchQuery ? filteredFolders.length > 0 : sortedRootFolders.length > 0) && (
                                            <>
                                                <p className={cn(librarySectionHeadingClassName, "px-2")}>Folders</p>
                                    <SortableContext
                                                    items={
                                                        searchQuery
                                                            ? filteredFolders.map((f) => f.id)
                                                            : sortedRootFolders.map((f) => f.id)
                                                    }
                                        strategy={verticalListSortingStrategy}
                                    >
                                                    {(searchQuery ? filteredFolders : sortedRootFolders).map((folder) => (
                                                <SortableFolderTreeItem
                                                    key={folder.id}
                                                    folder={folder}
                                                    allFolders={folders}
                                                    level={0}
                                                    onSelect={handleSelectFolder}
                                                    onEdit={handleEditFolder}
                                                    onDelete={handleDeleteFolder}
                                                    onNewSubfolder={handleNewSubfolder}
                                                    onDocumentClick={handleDocumentClick}
                                                    activeId={activeId}
                                                    overId={overId}
                                                    getItemCount={getFolderItemCount}
                                                    getItems={getFolderItems}
                                                />
                                                    ))}
                                                </SortableContext>
                                            </>
                                        )}
                                        {searchQuery && filteredFolders.length === 0 && filteredSpacesList.length === 0 && (
                                            <div className="px-2 py-2 text-sm text-muted-foreground text-center">
                                                No folders or spaces found
                                            </div>
                                        )}
                                        {filteredSpacesList.length > 0 && (
                                            <div className="space-y-1">
                                                <p className={cn(librarySectionHeadingClassName, "px-2")}>Spaces</p>
                                                {filteredSpacesList.map((space) => {
                                                    const spaceItemCount = getSpaceItemCount(space.id);
                                                    const spaceItems = getSpaceItems(space.id);
                                                    const topicsInSpace = (topics || []).filter(
                                                        (t) => t.spaceId === space.id
                                                    );
                                                    const hasTopics = topicsInSpace.length > 0;
                                                    const hasDocs = spaceItemCount > 0;
                                                    const isSpaceExpanded = expandedFolderIds.has(`space-${space.id}`);
                                                    const isDocsExpanded = expandedFolderIds.has(`space-${space.id}-docs`);
                                                    return (
                                                        <div key={space.id} className="mb-1">
                                                            <SidebarSpaceDropShell
                                                                spaceId={space.id}
                                                                className={cn(
                                                                    spaceNavRowClassName,
                                                                    selectedFolderId === `space-${space.id}` &&
                                                                        spaceNavRowSelectedClassName
                                                                )}
                                                                data-testid={`folder-space-${space.id}`}
                                                            >
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-5 w-5 p-0 shrink-0"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (hasDocs || hasTopics) {
                                                                            toggleFolderExpanded(`space-${space.id}`);
                                                                        }
                                                                    }}
                                                                >
                                                                    {hasDocs || hasTopics ? (
                                                                        isSpaceExpanded ? (
                                                                            <ChevronDown className="h-3.5 w-3.5" />
                                                                        ) : (
                                                                            <ChevronRight className="h-3.5 w-3.5" />
                                                                        )
                                                                    ) : (
                                                                        <div className="w-3.5" />
                                                                    )}
                                                                </Button>
                                                                <div
                                                                    className="flex items-center gap-2 flex-1 min-w-0"
                                                                    onClick={() => {
                                                                        setSelectedFolderId(`space-${space.id}`);
                                                                        navigate("/library");
                                                                    }}
                                                                >
                                                                    <Brain className="h-4 w-4 text-purple-500 shrink-0" />
                                                                    <span className={spaceNavLabelClassName}>
                                                                        {space.name}
                                                                    </span>
                                                                    {(hasDocs || hasTopics) && (
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="text-xs h-5 px-1.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 shrink-0"
                                                                        >
                                                                            {hasTopics && `${topicsInSpace.length} topic${topicsInSpace.length === 1 ? "" : "s"}`}
                                                                            {hasTopics && hasDocs && " · "}
                                                                            {hasDocs && `${spaceItemCount} doc${spaceItemCount === 1 ? "" : "s"}`}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </SidebarSpaceDropShell>

                                                            {/* Topics always listed under the space (drop targets for linking) */}
                                                            {hasTopics && (
                                                                <div
                                                                    className="mt-0.5 space-y-0.5 ml-5 border-l-2 border-purple-200/60 dark:border-purple-800/60 pl-2"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {topicsInSpace.map((topic) => {
                                                                        const topicItemCount = getTopicItemCount(topic.id);
                                                                        const topicItems = getTopicItems(topic.id);
                                                                        const isTopicExpanded = expandedFolderIds.has(`topic-${topic.id}`);
                                                                        const isActive = activeTopicFilterId === topic.id;
                                                                        return (
                                                                            <div key={topic.id}>
                                                                                <SidebarTopicDropShell
                                                                                    topicId={topic.id}
                                                                                    spaceId={topic.spaceId}
                                                                                    className={cn(
                                                                                        topicNavRowClassName,
                                                                                        isActive && topicNavRowSelectedClassName
                                                                                    )}
                                                                                >
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="icon"
                                                                                        className="h-5 w-5 p-0 shrink-0"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            toggleFolderExpanded(`topic-${topic.id}`);
                                                                                        }}
                                                                                    >
                                                                                        {topicItemCount > 0 ? (
                                                                                            isTopicExpanded ? (
                                                                                                <ChevronDown className="h-3.5 w-3.5" />
                                                                                            ) : (
                                                                                                <ChevronRight className="h-3.5 w-3.5" />
                                                                                            )
                                                                                        ) : (
                                                                                            <div className="w-3.5" />
                                                                                        )}
                                                                                    </Button>
                                                                                    <Tag className="h-3.5 w-3.5 text-primary shrink-0" />
                                                                                    <span
                                                                                        className="text-sm truncate flex-1 min-w-0 cursor-pointer rounded-full border border-border bg-muted/40 px-2 py-0.5 hover:bg-muted"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            startTransition(() => {
                                                                                                setSelectedFolderId(null);
                                                                                                navigate(
                                                                                                    `/library?topic=${encodeURIComponent(topic.id)}`
                                                                                                );
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        {topic.name || "Unnamed Topic"}
                                                                                    </span>
                                                                                    {topicItemCount > 0 && (
                                                                                        <Badge variant="secondary" className="text-xs h-5 px-1.5 shrink-0">
                                                                                            {topicItemCount}
                                                                                        </Badge>
                                                                                    )}
                                                                                </SidebarTopicDropShell>
                                                                                {topicItemCount > 0 && isTopicExpanded && (
                                                                                    <div className="mt-0.5 space-y-0.5">
                                                                                        {topicItems.slice(0, 10).map((item) => (
                                                                                            <div
                                                                                                key={item.id}
                                                                                                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer"
                                                                                                style={{ paddingLeft: "28px" }}
                                                                                                onClick={() => handleDocumentClick(item.id, item.type === "reference" ? "reference" : "note")}
                                                                                            >
                                                                                                <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                                                                                                <span className="text-xs truncate flex-1">{item.title}</span>
                                                                                                <Badge variant="outline" className="text-[10px] h-4 px-1">
                                                                                                    {item.type === "note" ? "Note" : "Ref"}
                                                                                                </Badge>
                                                                                            </div>
                                                                                        ))}
                                                                                        {topicItems.length > 10 && (
                                                                                            <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: "28px" }}>
                                                                                                +{topicItems.length - 10} more
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}

                                                            {/* Documents linked to this space (expand with space chevron) */}
                                                            {isSpaceExpanded && hasDocs && (
                                                                <div className="mt-0.5 ml-5 pl-2" style={{ paddingLeft: "8px" }}>
                                                                    <div
                                                                        className="group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFolderExpanded(`space-${space.id}-docs`);
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-5 w-5 p-0 shrink-0"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFolderExpanded(`space-${space.id}-docs`);
                                                                            }}
                                                                        >
                                                                            {isDocsExpanded ? (
                                                                                <ChevronDown className="h-3.5 w-3.5" />
                                                                            ) : (
                                                                                <ChevronRight className="h-3.5 w-3.5" />
                                                                            )}
                                                                        </Button>
                                                                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                                                        <span className="text-sm truncate flex-1 text-muted-foreground">
                                                                            Documents
                                                                        </span>
                                                                        <Badge variant="secondary" className="text-xs h-5 px-1.5">
                                                                            {spaceItemCount}
                                                                        </Badge>
                                                                    </div>
                                                                    {isDocsExpanded && (
                                                                        <div className="mt-0.5 space-y-0.5">
                                                                            {spaceItems.slice(0, 20).map((item) => (
                                                                                <div
                                                                                    key={item.id}
                                                                                    className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer"
                                                                                    style={{ paddingLeft: "40px" }}
                                                                                    onClick={() => handleDocumentClick(item.id, item.type === "reference" ? "reference" : "note")}
                                                                                >
                                                                                    <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                                                                                    <span className="text-xs truncate flex-1">{item.title}</span>
                                                                                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                                                                                        {item.type === "note" ? "Note" : "Ref"}
                                                                                    </Badge>
                                                                                </div>
                                                                            ))}
                                                                            {spaceItems.length > 20 && (
                                                                                <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: "40px" }}>
                                                                                    +{spaceItems.length - 20} more
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    )}
                                    </>
                                    )}
                            </div>

                            {/* TOPICS - shown after SPACES (commented out)
                            {Array.isArray(topics) && topics.length > 0 && (
                                <div className="mt-1 ml-4">
                                    <div
                                        className={cn(
                                            "group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                        )}
                                        data-testid="folder-topics"
                                    >
                                        <div className="w-[18px] shrink-0" />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 p-0 shrink-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFolderExpanded("topics");
                                            }}
                                            data-testid="toggle-topics"
                                        >
                                            {expandedFolderIds.has("topics") ? (
                                                <ChevronDown className="h-3.5 w-3.5"/>
                                            ) : (
                                                <ChevronRight className="h-3.5 w-3.5"/>
                                            )}
                                        </Button>
                                        <Bookmark className="h-4 w-4 text-amber-500 shrink-0"/>
                                        <span
                                            className="text-sm truncate flex-1 min-w-0 cursor-pointer"
                                            onClick={() => navigate("/library")}
                                        >
                                            TOPICS
                                        </span>
                                        <Badge variant="secondary" className="text-xs h-5 px-1.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                                            {topics.length}
                                        </Badge>
                                    </div>
                                    {expandedFolderIds.has("topics") && (
                                        <div className="mt-0.5 space-y-0.5">
                                            {[...topics].sort((a, b) => {
                                                const an = (a && a.name) || "";
                                                const bn = (b && b.name) || "";
                                                if (sortOrder === "a-z") return an.localeCompare(bn);
                                                if (sortOrder === "z-a") return bn.localeCompare(an);
                                                const aDate = (a && a.updatedAt) ? new Date(a.updatedAt).getTime() : 0;
                                                const bDate = (b && b.updatedAt) ? new Date(b.updatedAt).getTime() : 0;
                                                return bDate - aDate;
                                            }).map((topic) => {
                                                if (!topic || !topic.id) return null;
                                                const topicItemCount = getTopicItemCount(topic.id);
                                                const topicItems = getTopicItems(topic.id);
                                                const hasItems = topicItemCount > 0;
                                                const isTopicExpanded = expandedFolderIds.has(`topic-${topic.id}`);
                                                const topicName = topic.name || "Unnamed Topic";
                                                return (
                                                    <div key={topic.id}>
                                                        <div
                                                            className={cn(
                                                                "group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                                            )}
                                                            style={{paddingLeft: "16px"}}
                                                            data-testid={`folder-topic-${topic.id}`}
                                                        >
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-5 w-5 p-0"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFolderExpanded(`topic-${topic.id}`);
                                                                }}
                                                            >
                                                                {hasItems ? (
                                                                    isTopicExpanded ? (
                                                                        <ChevronDown className="h-3.5 w-3.5"/>
                                                                    ) : (
                                                                        <ChevronRight className="h-3.5 w-3.5"/>
                                                                    )
                                                                ) : (
                                                                    <div className="w-3.5"/>
                                                                )}
                                                            </Button>
                                                            <div
                                                                className="flex items-center gap-2 flex-1"
                                                                onClick={() => {
                                                                    startTransition(() => {
                                                                        setSelectedFolderId(null);
                                                                        navigate(`/library?topic=${encodeURIComponent(topic.id)}`);
                                                                    });
                                                                }}
                                                            >
                                                                <Bookmark className="h-4 w-4 text-amber-400 shrink-0"/>
                                                                <span className="text-sm truncate flex-1">{topicName}</span>
                                                                {topicItemCount > 0 && (
                                                                    <Badge variant="secondary" className="text-xs h-5 px-1.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                                                                        {topicItemCount}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {hasItems && isTopicExpanded && (
                                                            <div className="mt-0.5 space-y-0.5">
                                                                {topicItems.slice(0, 10).map((item) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer"
                                                                        style={{paddingLeft: "48px"}}
                                                                        onClick={() => handleDocumentClick(item.id, item.type === "reference" ? "reference" : "note")}
                                                                    >
                                                                        <FileText className="h-3 w-3 text-muted-foreground shrink-0"/>
                                                                        <span className="text-xs truncate flex-1">{item.title}</span>
                                                                        <Badge variant="outline" className="text-[10px] h-4 px-1">
                                                                            {item.type === "note" ? "Note" : "Ref"}
                                                                        </Badge>
                                                                    </div>
                                                                ))}
                                                                {topicItems.length > 10 && (
                                                                    <div className="px-2 py-1 text-xs text-muted-foreground" style={{paddingLeft: "48px"}}>
                                                                        +{topicItems.length - 10} more
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            */}
                        </>
                    )}

                    {/* AI Toolkit Section with Submenu */}
                    <div>
                        <div
                            className="flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                            data-testid="toggle-ai-toolkit"
                        >
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAiToolkitOpen(!aiToolkitOpen);
                                }}
                                data-testid="button-toggle-ai-toolkit"
                            >
                                {aiToolkitOpen ? (
                                    <ChevronDown className="h-3.5 w-3.5"/>
                                ) : (
                                    <ChevronRight className="h-3.5 w-3.5"/>
                                )}
                            </Button>
                            <div 
                                className="flex items-center gap-2 flex-1"
                                onClick={() => setAiToolkitOpen(!aiToolkitOpen)}
                            >
                                <Brain className="h-4 w-4 text-purple-500"/>
                                <span className="text-sm">AI Toolkit</span>
                            </div>
                        </div>
                        {aiToolkitOpen && (
                            <div className="mt-1 space-y-0.5">
                                <div
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => navigate("/library?section=super-prompts")}
                                >
                                    <Wand2 className="h-4 w-4 text-pink-500"/>
                                    <span className="text-sm">Super Prompts</span>
                                </div>
                                <div
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => navigate("/ai-agents")}
                                >
                                    <Bot className="h-4 w-4 text-green-500"/>
                                    <span className="text-sm">AI Agents</span>
                                </div>
                                <div
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => navigate("/library?section=workflows")}
                                >
                                    <GitBranch className="h-4 w-4 text-orange-500"/>
                                    <span className="text-sm">Workflows</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Templates Section with Submenu */}
                    <div>
                        <div
                            className="flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                            data-testid="toggle-templates"
                        >
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTemplatesOpen(!templatesOpen);
                                }}
                                data-testid="button-toggle-templates"
                            >
                                {templatesOpen ? (
                                    <ChevronDown className="h-3.5 w-3.5"/>
                                ) : (
                                    <ChevronRight className="h-3.5 w-3.5"/>
                                )}
                            </Button>
                            <div 
                                className="flex items-center gap-2 flex-1"
                                onClick={() => setTemplatesOpen(!templatesOpen)}
                            >
                                <Sparkles className="h-4 w-4 text-amber-500"/>
                                <span className="text-sm">Templates</span>
                            </div>
                        </div>
                        {templatesOpen && (
                            <div className="mt-1 space-y-0.5">
                                <div
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => navigate("/library?section=blueprints")}
                                >
                                    <Sparkles className="h-4 w-4 text-amber-400"/>
                                    <span className="text-sm">Browse Templates</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Integrations Section */}
                    <div>
                        <div
                            className="flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors"
                            data-testid="toggle-integrations"
                        >
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIntegrationsOpen(!integrationsOpen);
                                }}
                                data-testid="button-toggle-integrations"
                            >
                                {integrationsOpen ? (
                                    <ChevronDown className="h-3.5 w-3.5"/>
                                ) : (
                                    <ChevronRight className="h-3.5 w-3.5"/>
                                )}
                            </Button>
                            <div 
                                className="flex items-center gap-2 flex-1"
                                onClick={() => setIntegrationsOpen(!integrationsOpen)}
                            >
                                <GitBranch className="h-4 w-4 text-blue-500"/>
                                <span className="text-sm">Integrations</span>
                            </div>
                        </div>
                        {integrationsOpen && (
                            <div className="mt-1 space-y-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start gap-2"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => navigate("/library?syncPlatform=notion")}
                                    data-testid="button-sync-notion"
                                >
                                    <SiNotion className="h-4 w-4"/>
                                    Notion
                                </Button>
                                
                                {githubStatus?.connected ? (
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-muted/30" style={{paddingLeft: "32px"}}>
                                            <div className="flex items-center gap-2">
                                                <SiGithub className="h-4 w-4" />
                                                <span className="text-sm">GitHub</span>
                                                {githubStatus.username && (
                                                    <span className="text-xs text-muted-foreground">({githubStatus.username})</span>
                                                )}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={handleDisconnectGitHub}
                                            >
                                                Disconnect
                                            </Button>
                                        </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start gap-2"
                                    style={{paddingLeft: "32px"}}
                                    onClick={() => setShowGitHubPicker(true)}
                                    disabled={isImportingGitHubFile}
                                    data-testid="button-sync-github"
                                >
                                    {isImportingGitHubFile ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <SiGithub className="h-4 w-4" />
                                    )}
                                            {isImportingGitHubFile ? "Importing..." : "Import from GitHub"}
                                </Button>
                                    </div>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start gap-2"
                                        style={{paddingLeft: "32px"}}
                                        onClick={handleConnectGitHub}
                                        data-testid="button-connect-github"
                                    >
                                        <SiGithub className="h-4 w-4" />
                                        Connect GitHub
                                    </Button>
                                )}
                                
                                {/* Figma - Coming Soon */}
                                <div 
                                    className="flex items-center gap-2 py-2 rounded-md bg-muted/50 opacity-60 cursor-not-allowed"
                                    style={{paddingLeft: "32px"}}
                                    data-testid="button-sync-figma"
                                >
                                    <SiFigma className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">Figma</span>
                                    <Badge variant="outline" className="text-[10px] h-4 px-1 ml-auto">
                                        Coming Soon
                                    </Badge>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ScrollArea>
            
            {githubStatus?.connected && (
            <GitHubFilePicker
                open={showGitHubPicker}
                onOpenChange={setShowGitHubPicker}
                onSelect={async (file) => {
                    setIsImportingGitHubFile(true);
                    try {
                        await sourcesService.createSource({
                            title: `${file.repo}/${file.path}`,
                            type: "github",
                            url: file.html_url,
                            content: file.content,
                            topics: [],
                            tags: [],
                        }, currentWorkspaceId ?? null);
                        
                        queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
                        
                        toast({
                            title: "GitHub file imported",
                            description: `Successfully imported ${file.name} from ${file.repo}`,
                        });
                    } catch (error: any) {
                        console.error("Error importing GitHub file:", error);
                        toast({
                            title: "Import failed",
                            description: error.message || "Failed to import file from GitHub",
                            variant: "destructive",
                        });
                    } finally {
                        setIsImportingGitHubFile(false);
                    }
                }}
            />
            )}

            <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Folder</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="folder-name">Folder Name</Label>
                            <Input
                                id="folder-name"
                                value={folderName}
                                onChange={(e) => setFolderName(e.target.value)}
                                placeholder="Enter folder name..."
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleCreateFolder();
                                }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="folder-parent">Parent Folder (optional)</Label>
                            <Select value={newFolderParentId || "root"} onValueChange={(value) => setNewFolderParentId(value === "root" ? null : value)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Root (no parent)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="root">Root (no parent)</SelectItem>
                                    {allFoldersFlat
                                        .filter(f => f.id !== editingFolder?.id) // Don't allow selecting self as parent
                                        .map((folder) => {
                                            const depth = folder.parentId ? allFoldersFlat.findIndex(f => f.id === folder.parentId) >= 0 ? 1 : 0 : 0;
                                            const indent = "  ".repeat(depth);
                                            return (
                                                <SelectItem key={folder.id} value={folder.id}>
                                                    {indent}{folder.name}
                                                </SelectItem>
                                            );
                                        })}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setNewFolderDialogOpen(false);
                            setNewFolderParentId(null);
                        }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateFolder}
                            disabled={createFolderMutation.isPending || !folderName.trim()}
                        >
                            {createFolderMutation.isPending ? "Creating..." : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editFolderDialogOpen} onOpenChange={setEditFolderDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Rename Folder</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-folder-name">Folder Name</Label>
                            <Input
                                id="edit-folder-name"
                                value={folderName}
                                onChange={(e) => setFolderName(e.target.value)}
                                placeholder="Enter folder name..."
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleUpdateFolder();
                                }}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditFolderDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateFolder}
                            disabled={updateFolderMutation.isPending || !folderName.trim()}
                        >
                            {updateFolderMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

